import { apiClient } from './apiClient';

// Types
export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    isActive: boolean;
    lastLoginDate?: string;
    createdDate: string;
}

interface LoginResponse {
    token: string;
    user: User;
}

interface CreateUserRequest {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    roles: string[];
    isActive: boolean;
}

interface UpdateUserRequest {
    email?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
    isActive?: boolean;
}

interface ChangePasswordRequest {
    userId: string;
    newPassword: string;
    confirmPassword: string;
}

class AuthService {
    // Authentication methods
    async login(email: string, password: string): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/auth/login', { email, password });
        return response.data;
    }

    async logout(): Promise<void> {
        await apiClient.post('/auth/logout');
    }

    async getCurrentUser(): Promise<User> {
        const response = await apiClient.get('/auth/me');
        return response.data;
    }

    async register(userData: CreateUserRequest): Promise<User> {
        const response = await apiClient.post('/auth/register', userData);
        return response.data;
    }

    // User management methods
    async getAllUsers(): Promise<User[]> {
        const response = await apiClient.get('/auth/users');
        return response.data;
    }

    async getUserById(userId: string): Promise<User> {
        const response = await apiClient.get(`/auth/users/${userId}`);
        return response.data;
    }

    async createUser(userData: CreateUserRequest): Promise<User> {
        const response = await apiClient.post('/auth/register', userData);
        return response.data;
    }

    async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
        const response = await apiClient.put(`/auth/users/${userId}`, userData);
        return response.data;
    }

    async deleteUser(userId: string): Promise<void> {
        await apiClient.delete(`/auth/users/${userId}`);
    }

    async changeUserPassword(changePasswordData: ChangePasswordRequest): Promise<void> {
        await apiClient.post('/auth/users/change-password', changePasswordData);
    }

    async resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
        const response = await apiClient.post(`/auth/users/${userId}/reset-password`);
        return response.data;
    }

    // Role management methods
    async getAllRoles(): Promise<string[]> {
        const response = await apiClient.get('/auth/roles');
        return response.data;
    }
}

export const authService = new AuthService();